/* PortalPreview — iter211
 * "Customer Story Preview" screen.
 *
 * FOH lands here from the FOH pricing screen (button: "Preview & Arrange
 * Customer View") BEFORE sending the quote to the customer.
 *
 * Capabilities (Phase 2 MVP):
 *   • Drag-and-drop reorder of JGs within Urgent / Recommended /
 *     Advisory bands.
 *   • Cross-band drag (Urgent ↔ Recommended ↔ Advisory) with confirm
 *     modal capturing a reason. Hard-blocked on locked JGs (DVSA
 *     Dangerous + below-1.6mm tyres) — a 🔒 chip renders on those
 *     cards and they're non-draggable across bands.
 *   • Multi-select + "Group for customer view" CTA that amalgamates
 *     selected JGs under one customer-facing card (Option C — soft
 *     tag).
 *   • Ungroup link on any existing amalgamation.
 *   • "Reset to default order" — clears all overrides + groups in one
 *     click.
 *   • "Send to customer" gate — commits any pending reorder/group
 *     changes by simply navigating back to the pricing screen (the
 *     backend persisted on each interaction).
 *
 * The screen reuses the existing portal preview data (red_items +
 * amber_items as the customer sees them) so what FOH arranges here is
 * EXACTLY what the customer will see on send.
 */

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Lock, GripVertical, ArrowUp, ArrowDown, RotateCcw,
         Users, Send, ChevronRight, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BANDS = [
  { key: "urgent",      title: "Urgent Attention",     pill: "bg-red-100 text-red-800 border-red-300" },
  { key: "recommended", title: "Recommended",          pill: "bg-amber-100 text-amber-800 border-amber-300" },
  { key: "advisory",    title: "Advisory",             pill: "bg-slate-100 text-slate-800 border-slate-300" },
];

const OVERRIDE_REASONS = [
  { key: "evidence_progressed",     label: "Tech evidence shows this has progressed since last service" },
  { key: "customer_evidence",       label: "Customer-supplied evidence (noise / vibration / mileage)" },
  { key: "imminent_failure_risk",   label: "Imminent failure risk — escalating for customer awareness" },
  { key: "no_symptoms_observed",    label: "No symptoms observed — de-escalating from automated band" },
  { key: "covered_at_next_service", label: "Recommended at next service interval, not immediate" },
  { key: "customer_aware_deferring", label: "Customer aware and deferring" },
  { key: "other_with_note",         label: "Other (see typed note)" },
];

// iter211an — Every band is a `useDroppable` container so cross-band
// drags STILL RESOLVE when the target band is empty (BJ73MPV bug:
// Amber was empty so dnd-kit couldn't find an `over.id` and the drop
// was silently ignored). The `data-band` payload lets handleDragEnd
// know which band a container-drop landed on.
const BAND_DROP_PREFIX = "band:";

function BandDroppable({ bandKey, children, isEmpty }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `${BAND_DROP_PREFIX}${bandKey}`,
    data: { bandKey },
  });
  return (
    <div
      ref={setNodeRef}
      className={`rounded-lg transition-colors ${
        isOver ? "bg-blue-50 ring-2 ring-blue-300" : ""
      } ${isEmpty ? "min-h-[64px] border-2 border-dashed border-slate-200" : ""}`}
      data-testid={`band-drop-${bandKey}`}
    >
      {children}
    </div>
  );
}

function ItemCard({ item, locks, selected, onToggleSelect, isDragging }) {
  // iter211b — Use jg_id when present (the only id the backend
  // re-order/override endpoints accept), but fall back to id so the
  // checkbox + drag-handle keys NEVER collapse to `undefined` for
  // older inspection-item payloads.
  const selectionId = item.jg_id || item.id;
  const lock = locks[selectionId];
  const isLocked = lock?.locked;
  const isAmalgam = item._is_amalgamation;
  const groupLabel = item.customer_presentation_group_label;

  const {
    attributes, listeners, setNodeRef, transform, transition,
  } = useSortable({ id: item._dnd_id, disabled: false });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const price = Number(item.item_total || item.total_inc_vat || item.price_inc_vat || 0);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border-2 rounded-lg p-3 mb-2 group ${
        selected ? "border-blue-500 ring-2 ring-blue-200" : "border-slate-200"
      } ${isAmalgam ? "border-emerald-300 bg-emerald-50" : ""}`}
      data-testid={`portal-preview-card-${selectionId}`}
    >
      <div className="flex items-start gap-2">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 -ml-1 rounded hover:bg-slate-100 shrink-0"
          aria-label="Drag to reorder"
          data-testid={`drag-handle-${selectionId}`}
        >
          <GripVertical className="w-4 h-4 text-slate-400" />
        </button>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="mt-1.5 shrink-0"
          data-testid={`select-${selectionId}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <div className="font-semibold text-sm text-slate-900 break-words flex-1">
              {item.title || item.label}
            </div>
            {isAmalgam && (
              <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-800 shrink-0">
                <Users className="w-3 h-3 inline mr-0.5" />
                Grouped × {item.sub_items?.length || 0}
              </span>
            )}
            {isLocked && (
              <span
                className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-700 text-white shrink-0"
                title={lock.lock_reason}
              >
                <Lock className="w-3 h-3 inline mr-0.5" />
                Locked
              </span>
            )}
          </div>
          {isLocked && (
            <div className="text-[10px] text-red-700 mt-0.5 italic">
              {lock.lock_reason}
            </div>
          )}
          {groupLabel && !isAmalgam && (
            <div className="text-[11px] text-emerald-700 mt-0.5">
              ↳ part of &quot;{groupLabel}&quot;
            </div>
          )}
          {isAmalgam && item.sub_items && (
            <ul className="mt-1 ml-2 text-[11px] text-slate-600 space-y-0.5">
              {item.sub_items.map((sub, i) => (
                <li key={sub.jg_id || sub.id || i}>
                  · {sub.title || sub.label}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-bold text-slate-900">
            £{price.toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
}

function SeverityModal({ open, item, currentBand, targetBand, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
      setNotes("");
    }
  }, [open]);

  if (!open) return null;
  const escalating = (targetBand === "urgent" && currentBand !== "urgent");
  const downgrading = (currentBand === "urgent" && targetBand !== "urgent");

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5" data-testid="severity-override-modal">
        <h3 className="text-lg font-bold text-slate-900 mb-1">
          {escalating ? "Escalating to Urgent" : downgrading ? "Downgrading from Urgent" : "Change severity"}
        </h3>
        <p className="text-sm text-slate-600 mb-3">
          Move <span className="font-semibold">&quot;{item?.title || item?.label}&quot;</span> from{" "}
          <span className="font-semibold">{currentBand}</span> →{" "}
          <span className="font-semibold">{targetBand}</span>. A reason is captured for the audit log.
        </p>
        <label className="block text-xs font-semibold text-slate-700 mb-1">Reason</label>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full border rounded px-2 py-1.5 text-sm mb-3"
          data-testid="severity-override-reason"
        >
          <option value="">— select a reason —</option>
          {OVERRIDE_REASONS.map((r) => (
            <option key={r.key} value={r.key}>{r.label}</option>
          ))}
        </select>
        <label className="block text-xs font-semibold text-slate-700 mb-1">Notes (optional)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full border rounded px-2 py-1.5 text-sm mb-3"
          placeholder="Anything the manager will want to see in the audit log…"
          data-testid="severity-override-notes"
        />
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm border rounded hover:bg-slate-100"
            data-testid="severity-cancel"
          >
            Cancel
          </button>
          <button
            disabled={!reason}
            onClick={() => onConfirm({ reason, notes })}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="severity-confirm"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function AmalgamateModal({ open, count, onCancel, onConfirm }) {
  const [label, setLabel] = useState("Front Suspension Service");
  useEffect(() => { if (open) setLabel(""); }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" data-testid="amalgamate-modal">
        <h3 className="text-lg font-bold text-slate-900 mb-2">
          Group {count} jobs for the customer
        </h3>
        <p className="text-sm text-slate-600 mb-3">
          These jobs will appear as ONE expandable card on the customer&apos;s portal,
          with one combined total. You can ungroup any time.
        </p>
        <label className="block text-xs font-semibold text-slate-700 mb-1">
          Customer-facing label
        </label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Front Suspension Service"
          className="w-full border rounded px-2 py-1.5 text-sm mb-3"
          data-testid="amalgamate-label"
          autoFocus
        />
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm border rounded hover:bg-slate-100" data-testid="amalgamate-cancel">
            Cancel
          </button>
          <button
            disabled={!label.trim()}
            onClick={() => onConfirm({ label: label.trim() })}
            className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40"
            data-testid="amalgamate-confirm"
          >
            Group for customer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PortalPreviewPage() {
  const { yestimateId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bandedItems, setBandedItems] = useState({ urgent: [], recommended: [], advisory: [] });
  const [locks, setLocks] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pendingOverride, setPendingOverride] = useState(null);   // {item, currentBand, targetBand}
  const [amalgamateModalOpen, setAmalgamateModalOpen] = useState(false);
  const [previewToken, setPreviewToken] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // The customer-portal payload renders exactly what the customer
      // sees. We use the FOH-preview proxy endpoint when available, or
      // we fall back to issuing a portal token + reading the public
      // approval payload.
      let payload = null;
      try {
        const r = await axios.get(`${API}/foh/yestimate/${yestimateId}/portal-preview`);
        payload = r.data;
      } catch {
        // Fallback: mint a token then read the public approval endpoint.
        const tr = await axios.post(`${API}/foh/yestimate/${yestimateId}/portal-token`);
        const token = tr.data?.token;
        setPreviewToken(token);
        const pr = await axios.get(`${API}/customer/approval/${token}`);
        payload = pr.data;
      }
      const red   = payload?.red_items   || [];
      const amber = payload?.amber_items || [];
      // The backend already applied the override + amalgamation overlay
      // and sorted within bands. Re-band here just by the override
      // field for safety (the BE may emit pre-overlay items on some
      // routes).
      const _band = (i) => (
        (i.portal_severity_override || "").toLowerCase() ||
        (i.status === "red" || i.status === "dangerous" ? "urgent" :
         i.status === "amber" ? "recommended" : "advisory")
      );
      const allItems = [...red, ...amber];
      const urgent      = [];
      const recommended = [];
      const advisory    = [];
      for (const it of allItems) {
        // iter211b — Stamp a stable selection key. Use jg_id when
        // present (matches the backend reorder/override endpoints),
        // fall back to id so we never collapse to `undefined` for
        // legacy inspection-item payloads. _dnd_id is the dnd-kit
        // sortable id; _sel_id is what the checkbox / locks lookup
        // / severity-override POST all key off.
        const _sel = it.jg_id || it.id;
        const item = { ...it, _dnd_id: `${_sel}`, _sel_id: _sel };
        const b = _band(item);
        if (b === "urgent") urgent.push(item);
        else if (b === "recommended") recommended.push(item);
        else advisory.push(item);
      }
      setBandedItems({ urgent, recommended, advisory });

      const lr = await axios.get(`${API}/foh/yestimate/${yestimateId}/portal/locks`);
      const lookup = {};
      (lr.data?.locks || []).forEach((l) => { lookup[l.jg_id] = l; });
      setLocks(lookup);
    } catch (e) {
      toast.error("Could not load portal preview: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [yestimateId]);

  useEffect(() => { refresh(); }, [refresh]);

  const persistOrder = async (newBanded) => {
    const orders = [];
    for (const bandKey of ["urgent", "recommended", "advisory"]) {
      (newBanded[bandKey] || []).forEach((it, idx) => {
        const _sel = it._sel_id || it.jg_id || it.id;
        if (_sel) orders.push({ jg_id: _sel, customer_display_order: (idx + 1) * 10 });
      });
    }
    try {
      await axios.patch(`${API}/foh/yestimate/${yestimateId}/portal/reorder`, { orders });
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // Find which band each id lives in, and indices.
    const findLoc = (id) => {
      for (const bandKey of ["urgent", "recommended", "advisory"]) {
        const idx = bandedItems[bandKey].findIndex((i) => i._dnd_id === id);
        if (idx !== -1) return { bandKey, idx };
      }
      return null;
    };
    const from = findLoc(active.id);
    if (!from) return;

    // iter211an — Cross-band drop into an EMPTY band lands on the
    // band-container droppable (id "band:<key>") rather than on a
    // sibling item. Resolve it here so the drop still registers.
    let to = null;
    const overId = String(over.id || "");
    if (overId.startsWith(BAND_DROP_PREFIX)) {
      const bandKey = overId.slice(BAND_DROP_PREFIX.length);
      to = { bandKey, idx: (bandedItems[bandKey] || []).length };
    } else {
      to = findLoc(over.id);
    }
    if (!to) return;

    if (from.bandKey === to.bandKey) {
      // Reorder within band.
      const next = arrayMove(bandedItems[from.bandKey], from.idx, to.idx);
      const updated = { ...bandedItems, [from.bandKey]: next };
      setBandedItems(updated);
      persistOrder(updated);
    } else {
      // Cross-band → check lock + open severity confirm modal.
      const item = bandedItems[from.bandKey][from.idx];
      const _sel = item._sel_id || item.jg_id || item.id;
      const lock = locks[_sel];
      if (lock?.locked && to.bandKey !== "urgent") {
        toast.error(lock.lock_reason || "This item is severity-locked.");
        return;
      }
      setPendingOverride({
        item,
        from,
        to,
        currentBand: from.bandKey,
        targetBand:  to.bandKey,
      });
    }
  };

  const confirmSeverityOverride = async ({ reason, notes }) => {
    const { item, from, to } = pendingOverride;
    const _sel = item._sel_id || item.jg_id || item.id;
    if (!_sel) {
      toast.error("Cannot move this item — missing job-group id.");
      setPendingOverride(null);
      return;
    }
    try {
      await axios.post(
        `${API}/foh/yestimate/${yestimateId}/portal/severity-override`,
        { jg_id: _sel, band: to.bandKey, reason, notes },
      );
      // Optimistic local update: move the card between bands.
      const fromList = bandedItems[from.bandKey].filter((_, i) => i !== from.idx);
      const toList = [...bandedItems[to.bandKey]];
      toList.splice(to.idx, 0, { ...item, portal_severity_override: to.bandKey });
      const updated = {
        ...bandedItems,
        [from.bandKey]: fromList,
        [to.bandKey]:   toList,
      };
      setBandedItems(updated);
      await persistOrder(updated);
      toast.success(`Moved to ${to.bandKey}`);
    } catch (e) {
      toast.error("Override failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setPendingOverride(null);
    }
  };

  const handleAmalgamate = async ({ label }) => {
    const ids = Array.from(selectedIds);
    try {
      await axios.post(
        `${API}/foh/yestimate/${yestimateId}/portal/amalgamate`,
        { jg_ids: ids, label },
      );
      toast.success(`Grouped ${ids.length} jobs as &quot;${label}&quot;`);
      setSelectedIds(new Set());
      setAmalgamateModalOpen(false);
      refresh();
    } catch (e) {
      toast.error("Group failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleUngroup = async (gid) => {
    if (!gid) return;
    if (!window.confirm("Ungroup this customer-facing group?")) return;
    try {
      await axios.post(`${API}/foh/yestimate/${yestimateId}/portal/ungroup/${gid}`);
      toast.success("Ungrouped");
      refresh();
    } catch (e) {
      toast.error("Ungroup failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all customer-view overrides (order + severity + groupings)? This cannot be undone.")) return;
    try {
      await axios.post(`${API}/foh/yestimate/${yestimateId}/portal/reset`);
      toast.success("Portal preview reset to defaults");
      setSelectedIds(new Set());
      refresh();
    } catch (e) {
      toast.error("Reset failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const allActiveAmalgams = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const bk of ["urgent", "recommended", "advisory"]) {
      for (const it of bandedItems[bk]) {
        if (it._is_amalgamation && it.customer_presentation_group_id && !seen.has(it.customer_presentation_group_id)) {
          seen.add(it.customer_presentation_group_id);
          out.push({
            id:    it.customer_presentation_group_id,
            label: it.title || it.label,
            count: it.sub_items?.length || 0,
          });
        }
      }
    }
    return out;
  }, [bandedItems]);

  if (loading) {
    return <div className="p-8 text-center text-slate-500">Loading customer preview…</div>;
  }

  const totalSelected = selectedIds.size;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-3 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3 flex-wrap">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="text-xs text-blue-700 hover:underline mb-0.5"
              data-testid="back-to-pricing"
            >
              ← Back to pricing
            </button>
            <h1 className="text-lg font-bold text-slate-900">
              Customer Story Preview
            </h1>
            <p className="text-xs text-slate-500">
              Drag to reorder · check items + "Group for customer" to amalgamate · cross-band drag requires a reason
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {totalSelected >= 2 && (
              <button
                onClick={() => setAmalgamateModalOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700"
                data-testid="amalgamate-cta"
              >
                <Users className="w-4 h-4" />
                Group {totalSelected} for customer
              </button>
            )}
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded hover:bg-slate-100"
              data-testid="reset-customer-view"
              title="Clear all order + severity + grouping overrides"
            >
              <RotateCcw className="w-4 h-4" />
              Reset to default
            </button>
            <button
              onClick={() => navigate(`/app/foh/yestimate/${yestimateId}/send-portal`)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              data-testid="send-to-customer-from-preview"
              title="Send the quote with the customer story you've arranged"
            >
              <Send className="w-4 h-4" />
              Send to customer
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        {allActiveAmalgams.length > 0 && (
          <div className="max-w-5xl mx-auto mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500">Active groups:</span>
            {allActiveAmalgams.map((g) => (
              <span
                key={g.id}
                className="inline-flex items-center gap-1 text-[11px] bg-emerald-100 text-emerald-800 border border-emerald-300 rounded px-2 py-0.5"
              >
                <Users className="w-3 h-3" />
                {g.label} · {g.count}
                <button
                  onClick={() => handleUngroup(g.id)}
                  className="ml-1 text-emerald-700 hover:text-emerald-900 underline"
                  data-testid={`ungroup-${g.id}`}
                >
                  ungroup
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {BANDS.map((b) => {
            const items = bandedItems[b.key] || [];
            return (
              <section key={b.key} className="mb-6" data-testid={`band-${b.key}`}>
                <div className="flex items-baseline gap-2 mb-2">
                  <h2 className={`text-sm font-bold uppercase tracking-wide inline-flex items-center px-2 py-0.5 rounded border ${b.pill}`}>
                    {b.title} · {items.length}
                  </h2>
                </div>
                <BandDroppable bandKey={b.key} isEmpty={items.length === 0}>
                  {items.length === 0 ? (
                    <div className="text-xs text-slate-400 italic py-3 px-2">
                      Drop items here to move them to {b.title.toLowerCase()}.
                    </div>
                  ) : (
                    <SortableContext
                      items={items.map((i) => i._dnd_id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {items.map((it) => {
                        const _sel = it._sel_id || it.jg_id || it.id;
                        return (
                          <ItemCard
                            key={it._dnd_id}
                            item={it}
                            locks={locks}
                            selected={selectedIds.has(_sel)}
                            onToggleSelect={() => {
                              if (!_sel) return;
                              const next = new Set(selectedIds);
                              if (next.has(_sel)) next.delete(_sel);
                              else next.add(_sel);
                              setSelectedIds(next);
                            }}
                          />
                        );
                      })}
                    </SortableContext>
                  )}
                </BandDroppable>
              </section>
            );
          })}
        </DndContext>
      </div>

      <SeverityModal
        open={!!pendingOverride}
        item={pendingOverride?.item}
        currentBand={pendingOverride?.currentBand}
        targetBand={pendingOverride?.targetBand}
        onCancel={() => setPendingOverride(null)}
        onConfirm={confirmSeverityOverride}
      />
      <AmalgamateModal
        open={amalgamateModalOpen}
        count={totalSelected}
        onCancel={() => setAmalgamateModalOpen(false)}
        onConfirm={handleAmalgamate}
      />
    </div>
  );
}
